/**
 * Blocks the authenticated app when account deletion must not use the product:
 * PENDING / PROCESSING / COMPLETED, or FAILED after DB wipe when Supabase auth delete did not succeed.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useRouter } from 'expo-router'
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext'
import { logger } from '../../src/helpers/logger.helper'
import { SignInLoadingScreen } from '../signIn/SignInLoadingScreen'
import { setPendingSignInBannerMessage } from '../../src/signIn/helpers/signInBannerTransfer.helper'
import {
  deletionJobStatusBlocksApp,
  fetchAccountDeletionJobStatusWithRetries,
  signInBannerForBlockedDeletionJobStatus,
} from '../../src/signIn/helpers/accountDeletionStatusApi.helper'

const LOG_PREFIX = '[AccountDeletionAccessGate]'

type GatePhase = 'checking' | 'ready'

export function AccountDeletionAccessGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, isAnonymous, userId, signOut, getToken } = useSupabaseAuth()
  const router = useRouter()
  const [phase, setPhase] = useState<GatePhase>('checking')
  const lastVerifiedUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isLoaded) {
      return
    }
    // Anonymous guests are brand-new sessions that can never have a deletion job — skip the
    // blocking status check entirely. This also avoids the synchronous loading screen below
    // (which unmounts the Tabs navigator and resets it to its initial route) when a guest
    // signs in and navigates straight into an authenticated route like /app/onboarding.
    if (!isSignedIn || !userId || isAnonymous) {
      lastVerifiedUserIdRef.current = null
      setPhase('ready')
      return
    }
    if (lastVerifiedUserIdRef.current === userId) {
      setPhase('ready')
      return
    }

    let cancelled = false
    setPhase('checking')

    void (async () => {
      try {
        const token = await getToken({ skipCache: true })
        if (!token) {
          if (!cancelled) setPhase('ready')
          return
        }

        const result = await fetchAccountDeletionJobStatusWithRetries(token)

        if (cancelled) return

        if (!result.ok) {
          if (!cancelled) {
            lastVerifiedUserIdRef.current = userId
            setPhase('ready')
            logger.warn(`${LOG_PREFIX} status check failed after retries, allowing access`, {})
          }
          return
        }

        const jobStatus = result.jobStatus
        if (
          jobStatus !== null &&
          deletionJobStatusBlocksApp(jobStatus, result.lastErrorCode)
        ) {
          lastVerifiedUserIdRef.current = null
          try {
            await signOut()
          } catch (e) {
            logger.error(`${LOG_PREFIX} signOut failed`, {
              error: e instanceof Error ? e.message : String(e),
            })
          }
          if (!cancelled) {
            setPendingSignInBannerMessage(
              signInBannerForBlockedDeletionJobStatus(jobStatus, result.lastErrorCode)
            )
            router.replace('/sign-in')
          }
          return
        }

        lastVerifiedUserIdRef.current = userId
        if (!cancelled) setPhase('ready')
      } catch (e) {
        if (!cancelled) {
          lastVerifiedUserIdRef.current = userId
          setPhase('ready')
        }
        if (!cancelled) {
          logger.warn(`${LOG_PREFIX} status check threw, allowing access`, {
            error: e instanceof Error ? e.message : String(e),
          })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, isAnonymous, userId, getToken, signOut, router])

  // Never block a guest with the loading screen — rendering it here (in place of children)
  // unmounts the Tabs navigator mid-transition and drops the just-issued route. Guests are
  // exempt from the deletion gate anyway (see the effect above).
  if (!isLoaded || (isSignedIn && !isAnonymous && phase === 'checking')) {
    return <SignInLoadingScreen />
  }

  return <>{children}</>
}
